import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Shield,
  Mail,
  ChevronRight,
  ArrowRight,
  Sparkles,
  Accessibility,
  Lock,
  HeartHandshake,
  Scale,
  Wallet,
} from "lucide-react";
import { Container, FadeIn, SectionLabel } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import {
  POLICIES,
  POLICY_GROUPS,
  POLICY_INDEX,
  SAFEGUARDING_EMAIL,
  PRIVACY_EMAIL,
  GENERAL_EMAIL,
} from "@/lib/policies";
import { IMG } from "@/lib/data";

const GROUP_ICONS = {
  "Safeguarding & Wellbeing": Shield,
  "Inclusion & Access": Accessibility,
  "Health, Safety & Operations": HeartHandshake,
  "Privacy, Data & Money": Lock,
  Governance: Scale,
};

export default function Policies() {
  const { slug } = useParams();
  const initialSlug =
    slug && POLICY_INDEX[slug] ? slug : POLICIES[0].slug;
  const [active, setActive] = useState(initialSlug);
  const policy = useMemo(() => POLICY_INDEX[active] || POLICIES[0], [active]);

  return (
    <div data-testid="policies-page" className="bg-[#050505]">
      <SEOHead
        title="Policies & Governance · Temple of Kulture · Safeguarding, GDPR, Accessibility & Immersive Safety"
        description="Our complete safeguarding, child protection, EDI, accessibility, GDPR, immersive experience safety, refunds and governance policies. Built for trust, transparency and funder confidence."
        image={IMG.heroPortal}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Temple of Kulture — Policies & Governance",
          about: POLICIES.map((p) => p.title),
          publisher: { "@type": "Organization", name: "Temple of Kulture" },
        }}
      />

      {/* HERO */}
      <section className="relative pt-40 sm:pt-48 pb-16 sm:pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${IMG.heroPortal})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/85 via-[#050505]/90 to-[#050505]" />
        <Container className="relative">
          <p className="tok-eyebrow flex items-center gap-3">
            <Shield className="h-3.5 w-3.5" />
            Policies & Governance
          </p>
          <h1
            data-testid="policies-headline"
            className="mt-6 tok-headline-xl max-w-4xl"
          >
            Cinematic on the outside. <br />
            <span className="text-amber-400">Safeguarded</span> on the inside.
          </h1>
          <p className="mt-8 text-zinc-300 leading-relaxed max-w-3xl text-lg">
            Temple of Kulture works with children, young people, families,
            schools, councils, NHS partners and immersive technology. Our
            policies hold all of that to the same standards as our creative
            work — clear, accessible, professionally operated.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={`mailto:${SAFEGUARDING_EMAIL}`}
              data-testid="policies-safeguarding-cta"
              className="inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
            >
              <Mail className="h-4 w-4" /> Safeguarding contact
            </a>
            <a
              href={`mailto:${PRIVACY_EMAIL}`}
              data-testid="policies-privacy-cta"
              className="inline-flex items-center gap-3 border border-amber-400/40 hover:border-amber-400 text-amber-400 hover:bg-amber-400/5 px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
            >
              <Lock className="h-4 w-4" /> Privacy & GDPR
            </a>
          </div>
        </Container>
      </section>

      {/* CONTENT GRID */}
      <section className="pb-24 sm:pb-32">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Sidebar — policy nav */}
            <aside
              className="lg:col-span-4 xl:col-span-3"
              data-testid="policies-sidebar"
            >
              <div className="lg:sticky lg:top-28 space-y-8">
                {POLICY_GROUPS.map((group) => {
                  const Icon = GROUP_ICONS[group.label] || Shield;
                  return (
                    <div key={group.label}>
                      <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold mb-4 flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5" />
                        {group.label}
                      </p>
                      <ul className="space-y-1">
                        {group.slugs.map((slug) => {
                          const p = POLICY_INDEX[slug];
                          if (!p) return null;
                          const isActive = active === slug;
                          return (
                            <li key={slug}>
                              <button
                                type="button"
                                data-testid={`policy-nav-${slug}`}
                                onClick={() => {
                                  setActive(slug);
                                  const el = document.getElementById(
                                    "policy-content",
                                  );
                                  if (el)
                                    el.scrollIntoView({
                                      behavior: "smooth",
                                      block: "start",
                                    });
                                }}
                                className={`group w-full text-left flex items-center justify-between gap-3 px-4 py-3 border transition-colors ${
                                  isActive
                                    ? "border-amber-400 bg-amber-400/10 text-amber-400"
                                    : "border-white/5 bg-zinc-950 text-zinc-300 hover:border-amber-400/40 hover:text-amber-400"
                                }`}
                              >
                                <span className="text-sm font-display font-semibold leading-tight">
                                  {p.title}
                                </span>
                                <ChevronRight
                                  className={`h-3.5 w-3.5 shrink-0 transition-transform ${
                                    isActive
                                      ? "translate-x-0.5"
                                      : "group-hover:translate-x-0.5"
                                  }`}
                                />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}

                {/* Quick-help card */}
                <div className="border border-amber-400/30 bg-zinc-950 p-6">
                  <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold">
                    Need a copy on letterhead?
                  </p>
                  <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                    Schools, councils, NHS teams and funders can request a
                    signed, dated PDF version of any policy below.
                  </p>
                  <a
                    href={`mailto:${GENERAL_EMAIL}?subject=Policy%20pack%20request`}
                    data-testid="policies-request-pdf"
                    className="mt-5 inline-flex items-center gap-2 tok-cta-label text-amber-400 hover:gap-3 transition-all"
                  >
                    Request policy pack <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </aside>

            {/* Active policy panel */}
            <div className="lg:col-span-8 xl:col-span-9" id="policy-content">
              <FadeIn key={policy.slug}>
                <article
                  data-testid={`policy-article-${policy.slug}`}
                  className="border border-white/5 bg-zinc-950 p-8 sm:p-12"
                >
                  <p className="tok-eyebrow-sm">{policy.eyebrow}</p>
                  <h2
                    className="mt-5 font-display font-black uppercase leading-[1] tracking-tighter text-3xl sm:text-4xl lg:text-5xl"
                    data-testid="policy-title"
                  >
                    {policy.title}
                  </h2>
                  <p className="mt-6 text-zinc-200 text-lg leading-relaxed">
                    {policy.summary}
                  </p>

                  <div className="mt-10 space-y-8">
                    {policy.body.map((section, i) => (
                      <div key={i}>
                        {section.h && (
                          <h3 className="font-display font-bold uppercase text-amber-400 text-sm tracking-[0.2em]">
                            {section.h}
                          </h3>
                        )}
                        {section.p && (
                          <p className="mt-3 text-zinc-300 leading-relaxed">
                            {section.p}
                          </p>
                        )}
                        {section.list && (
                          <ul className="mt-3 space-y-2.5 text-zinc-300 leading-relaxed">
                            {section.list.map((li, j) => (
                              <li
                                key={j}
                                className="flex gap-3 items-start"
                              >
                                <span className="text-amber-400 mt-2 inline-block h-1 w-3 bg-amber-400 shrink-0" />
                                <span>{li}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-12 pt-8 border-t border-white/5 flex flex-wrap gap-3 text-xs text-zinc-500">
                    <span className="uppercase tracking-[0.25em] font-bold">
                      Reviewed annually
                    </span>
                    <span>·</span>
                    <span>
                      Questions?{" "}
                      <a
                        href={`mailto:${GENERAL_EMAIL}`}
                        className="text-amber-400 hover:underline"
                      >
                        {GENERAL_EMAIL}
                      </a>
                    </span>
                  </div>
                </article>

                {/* Immersive-safety surfacing */}
                {policy.slug !== "immersive-safety" && (
                  <button
                    type="button"
                    onClick={() => {
                      setActive("immersive-safety");
                      const el = document.getElementById("policy-content");
                      if (el)
                        el.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                    }}
                    data-testid="policies-immersive-cta"
                    className="mt-8 group block w-full text-left border border-amber-400/30 hover:border-amber-400 bg-amber-400/5 hover:bg-amber-400/10 p-6 sm:p-8 transition-colors"
                  >
                    <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold">
                      Coming to a TOK experience?
                    </p>
                    <h3 className="mt-3 font-display font-bold text-xl sm:text-2xl">
                      Read our Immersive Experience Safety guide
                    </h3>
                    <p className="mt-3 text-sm text-zinc-300 max-w-xl">
                      VR, projection, lighting and sound — what to expect,
                      sensory considerations and how we keep everyone safe.
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 tok-cta-label text-amber-400 group-hover:gap-3 transition-all">
                      <Sparkles className="h-3.5 w-3.5" /> Read immersive safety
                      guide
                    </span>
                  </button>
                )}
              </FadeIn>
            </div>
          </div>
        </Container>
      </section>

      {/* Trust strip */}
      <section className="border-t border-amber-500/10 bg-black py-16">
        <Container>
          <SectionLabel>Funder & partner confidence</SectionLabel>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <TrustCell
              Icon={Shield}
              t="Safeguarding"
              d="DBS-checked staff, designated lead, annual review."
            />
            <TrustCell
              Icon={Accessibility}
              t="Accessibility"
              d="WCAG 2.2 AA target; step-free, sensory-aware sessions."
            />
            <TrustCell
              Icon={Lock}
              t="GDPR"
              d="Lawful basis, explicit opt-in, ICO-aligned."
            />
            <TrustCell
              Icon={Wallet}
              t="Financial"
              d="Dual authorisation, restricted-fund tracking, audited."
            />
          </div>
          <p className="mt-10 text-xs text-zinc-500 max-w-3xl leading-relaxed">
            Temple of Kulture is committed to working transparently with Arts
            Council England, the BFI, local authorities, schools, NHS partners
            and our community. If you are exploring a partnership, get in touch
            and we will share signed policy documentation as part of due
            diligence.
          </p>
        </Container>
      </section>
    </div>
  );
}

function TrustCell({ Icon, t, d }) {
  return (
    <div className="border border-white/5 bg-zinc-950 p-6">
      <Icon className="h-6 w-6 text-amber-400" />
      <p className="mt-4 font-display font-bold text-sm uppercase tracking-[0.15em]">
        {t}
      </p>
      <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{d}</p>
    </div>
  );
}
